"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useClientSidebar } from "./client-sidebar-context";
import { CreateMenu } from "@/components/topbar/create-menu";
import { TopBarClientSwitcher } from "@/components/topbar/client-switcher";
import { DemoToggle } from "@/components/topbar/demo-toggle";
import { ClientModeToggle } from "@/components/topbar/client-mode-toggle";
import { HelpCenter } from "@/components/topbar/help-center";
import type { ResolvedClientMode } from "@/lib/auth/client-mode-shared";
import { COMPACT_NAV_MEDIA_QUERY, DESKTOP_NAV_MEDIA_QUERY } from "@/lib/nav/responsive";

interface MobileNavDrawerProps {
  nav: ReactNode;
  role: string;
  name: string | null;
  isStaff: boolean;
  clientModeEligible: boolean;
  clientMode: ResolvedClientMode | null;
  pendingApprovals: number;
  approvalsHref: string;
  triggerRef: RefObject<HTMLButtonElement | null>;
}

/** Compact-screen application navigation. Kept separate from the persisted desktop rail. */
export function MobileNavDrawer({
  nav,
  role,
  name,
  isStaff,
  clientModeEligible,
  clientMode,
  pendingApprovals,
  approvalsHref,
  triggerRef,
}: MobileNavDrawerProps) {
  const { mobileNavOpen, setMobileNavOpen } = useClientSidebar();
  const pathname = usePathname();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousPathRef = useRef(pathname);

  useEffect(() => {
    if (pathname === previousPathRef.current) return;
    previousPathRef.current = pathname;
    setMobileNavOpen(false);
  }, [pathname, setMobileNavOpen]);

  useEffect(() => {
    if (!mobileNavOpen) return;

    const previousOverflow = document.body.style.overflow;
    const trigger = triggerRef.current;
    document.body.style.overflow = "hidden";
    const focusFrame = requestAnimationFrame(() => closeRef.current?.focus());

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setMobileNavOpen(false);
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function onDesktopChange(event: MediaQueryListEvent) {
      if (event.matches) setMobileNavOpen(false);
    }

    const desktopQuery = window.matchMedia(DESKTOP_NAV_MEDIA_QUERY);
    desktopQuery.addEventListener("change", onDesktopChange);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      desktopQuery.removeEventListener("change", onDesktopChange);
      document.removeEventListener("keydown", onKeyDown);
      if (window.matchMedia(COMPACT_NAV_MEDIA_QUERY).matches) trigger?.focus();
    };
  }, [mobileNavOpen, setMobileNavOpen, triggerRef]);

  if (!mobileNavOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] lg:hidden">
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className="mobile-nav-fade-in absolute inset-0 bg-black/55 backdrop-blur-[1px]"
        onClick={() => setMobileNavOpen(false)}
      />
      <div
        ref={panelRef}
        id="mobile-navigation-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className="mobile-nav-slide-in relative flex h-full w-[min(90vw,23rem)] flex-col bg-spark-black shadow-[18px_0_48px_rgba(0,0,0,.38)]"
      >
        <div className="flex h-14 shrink-0 items-center border-b border-white/[.08] px-4">
          <Link
            href="/"
            onClick={() => setMobileNavOpen(false)}
            className="flex min-w-0 flex-1 items-center gap-2"
          >
            <Image src="/spark-logo.svg" alt="" width={13} height={22} className="shrink-0" />
            <span className="text-base font-extrabold tracking-wide text-white">SPARK</span>
            <span className="text-sm font-medium text-[#AAB2BD]">Dash</span>
          </Link>
          <button
            ref={closeRef}
            type="button"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close navigation"
            className="flex h-11 w-11 items-center justify-center rounded-lg text-[#AAB2BD] transition-colors hover:bg-white/[.06] hover:text-white"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="shrink-0 border-b border-white/[.08] p-3">
          <TopBarClientSwitcher variant="drawer" />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3">
          {nav}
        </div>

        <div className="shrink-0 border-t border-white/[.08] p-3">
          <p className="mb-1 px-3 text-[10px] font-bold uppercase tracking-[.1em] text-[#7D8693]">
            Utilities
          </p>
          <div className="grid grid-cols-2 gap-1">
            {isStaff && <CreateMenu variant="drawer" />}
            <HelpCenter name={name} role={role} variant="drawer" />
            {isStaff && <DemoToggle variant="drawer" />}
            {clientModeEligible && <ClientModeToggle mode={clientMode} variant="drawer" />}
            {pendingApprovals > 0 && (
              <Link
                href={approvalsHref}
                onClick={() => setMobileNavOpen(false)}
                className="col-span-2 flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium text-[#C3CAD3] transition-colors hover:bg-white/[.06] hover:text-white"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <span className="flex-1">Approvals</span>
                <span className="rounded-full bg-spark-green px-1.5 py-px text-[10px] font-bold text-spark-ink tnum">
                  {pendingApprovals}
                </span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
